import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PaperPlaneTilt } from "@phosphor-icons/react";

// Aggregates the 3 template sections:
//   1. Main "SPB Baru" message template + placeholders
//   2. Reminder "SPB belum termuat" toggle + threshold + template
//   3. Cancel + Edit SPB lifecycle notification templates
// Placeholder legend arrays are passed in so the parent stays the source
// of truth for the vocabulary used across the app.
export default function WATemplatesPanel({
  form, setField,
  onRunReminders, runningReminder,
  placeholders,      // main
  reminderPlaceholders,
  cancelPlaceholders,
  editPlaceholders,
}) {
  return (
    <>
      {/* Template */}
      <section className="border border-border bg-card p-5 space-y-4">
        <div>
          <div className="overline">Template Pesan</div>
          <div className="text-xs text-muted-foreground mt-1">
            Klik chip di bawah untuk menyisipkan placeholder. Gunakan <code>*text*</code> untuk bold di WhatsApp.
          </div>
        </div>
        <Textarea
          value={form.template}
          onChange={(e) => setField("template", e.target.value)}
          rows={9}
          className="rounded-xl font-mono text-sm"
          data-testid="wa-template-textarea"
        />
        <div className="flex flex-wrap gap-1.5">
          {placeholders.map(([ph, desc]) => (
            <button
              type="button"
              key={ph}
              title={desc}
              onClick={() => setField("template", (form.template || "") + ph)}
              className="text-[11px] font-mono bg-secondary hover:bg-primary hover:text-primary-foreground border border-border px-2 py-1"
              data-testid={`placeholder-${ph}`}
            >
              {ph}
            </button>
          ))}
        </div>
      </section>

      {/* Reminder */}
      <section className="border border-border bg-card p-5 space-y-4" data-testid="reminder-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-bold text-sm flex items-center gap-2">
              ⏰ Reminder SPB Belum Termuat
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Kirim WA otomatis ke petugas kalau SPB masih <span className="font-bold">belum termuat</span> lebih dari ambang batas waktu. Satu SPB hanya dikirimi reminder sekali.
            </div>
          </div>
          <Switch
            checked={form.reminder_enabled}
            onCheckedChange={(v) => setField("reminder_enabled", v)}
            data-testid="wa-reminder-switch"
          />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm mb-2 block">Ambang Batas (menit)</Label>
            <Input
              type="number"
              min={1}
              max={1440}
              value={form.reminder_after_minutes}
              onChange={(e) => setField("reminder_after_minutes", parseInt(e.target.value, 10) || 30)}
              className="rounded-xl h-11 font-mono"
              disabled={!form.reminder_enabled}
              data-testid="wa-reminder-minutes"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Reminder dikirim setelah SPB berumur ≥ {form.reminder_after_minutes || 30} menit.
            </p>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button
              type="button"
              variant="outline"
              onClick={onRunReminders}
              disabled={runningReminder || !form.reminder_enabled || !form.enabled}
              className="rounded-xl h-11"
              data-testid="wa-reminder-run-now"
            >
              <PaperPlaneTilt size={16} className="mr-2" />
              {runningReminder ? "Memindai..." : "Jalankan Sekarang"}
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-sm mb-2 block">Template Reminder</Label>
          <Textarea
            value={form.reminder_template}
            onChange={(e) => setField("reminder_template", e.target.value)}
            rows={7}
            className="rounded-xl font-mono text-sm"
            disabled={!form.reminder_enabled}
            data-testid="wa-reminder-template"
          />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {reminderPlaceholders.map(([ph, desc]) => (
              <button
                type="button"
                key={ph}
                title={desc}
                disabled={!form.reminder_enabled}
                onClick={() => setField("reminder_template", (form.reminder_template || "") + ph)}
                className="text-[11px] font-mono bg-secondary hover:bg-primary hover:text-primary-foreground border border-border px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`reminder-placeholder-${ph}`}
              >
                {ph}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Template Pembatalan & Edit SPB */}
      <section className="border border-border bg-card p-6 space-y-4">
        <div>
          <div className="overline">Template Notifikasi Lifecycle SPB</div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mt-1">Pembatalan &amp; Perubahan</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Template terpisah untuk event <strong>SPB dibatalkan</strong> dan <strong>SPB dirubah</strong>. Kosongkan untuk pakai default sistem.
          </p>
        </div>
        <div>
          <Label className="text-sm mb-2 block">Template Pembatalan SPB</Label>
          <Textarea
            value={form.cancel_template || ""}
            onChange={(e) => setField("cancel_template", e.target.value)}
            rows={8}
            placeholder="Kosongkan untuk pakai default sistem"
            className="rounded-xl font-mono text-sm"
            data-testid="wa-cancel-template"
          />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {cancelPlaceholders.map(([ph, desc]) => (
              <button
                type="button"
                key={ph}
                title={desc}
                onClick={() => setField("cancel_template", (form.cancel_template || "") + ph)}
                className="text-[11px] font-mono bg-secondary hover:bg-primary hover:text-primary-foreground border border-border px-2 py-1"
                data-testid={`cancel-placeholder-${ph}`}
              >
                {ph}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm mb-2 block">Template Perubahan (Edit) SPB</Label>
          <Textarea
            value={form.edit_template || ""}
            onChange={(e) => setField("edit_template", e.target.value)}
            rows={8}
            placeholder="Kosongkan untuk pakai default sistem"
            className="rounded-xl font-mono text-sm"
            data-testid="wa-edit-template"
          />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {editPlaceholders.map(([ph, desc]) => (
              <button
                type="button"
                key={ph}
                title={desc}
                onClick={() => setField("edit_template", (form.edit_template || "") + ph)}
                className="text-[11px] font-mono bg-secondary hover:bg-primary hover:text-primary-foreground border border-border px-2 py-1"
                data-testid={`edit-placeholder-${ph}`}
              >
                {ph}
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
